import React, { useState } from 'react';
import { Navigation } from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { fileService } from '../services/fileService';
import './Dashboard.css';

export const Playbook: React.FC = () => {
  const { token } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [loadingPodcast, setLoadingPodcast] = useState(false);
  const [podcastUrl, setPodcastUrl] = useState<string | null>(null);
  const [loadingDebate, setLoadingDebate] = useState(false);
  const [debateUrl, setDebateUrl] = useState<string | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryUrl, setSummaryUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!token) return;
    setDownloading(true);
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'playbook.pdf');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError('Failed to open playbook. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleListenPodcast = async () => {
    if (!token) return;
    setLoadingPodcast(true);
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'playbook-podcast.m4a');
      setPodcastUrl(url);
    } catch (err) {
      setError('Failed to load podcast. Please try again.');
    } finally {
      setLoadingPodcast(false);
    }
  };

  const handleListenDebate = async () => {
    if (!token) return;
    setLoadingDebate(true);
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'Why_Boring_Investing_Beats_Optimization.m4a');
      setDebateUrl(url);
    } catch (err) {
      setError('Failed to load debate. Please try again.');
    } finally {
      setLoadingDebate(false);
    }
  };

  const handleListenSummary = async () => {
    if (!token) return;
    setLoadingSummary(true);
    setError(null);
    try {
      const url = await fileService.getFileUrl(token, 'playbook-summary.m4a');
      setSummaryUrl(url);
    } catch (err) {
      setError('Failed to load summary. Please try again.');
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div className="welcome-section">
          <h2>Playbook</h2>
          <p>Your reference document for how this financial system operates.</p>
          <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            By Bobby Malik &nbsp;&middot;&nbsp; &copy; 2026. All rights reserved.
          </p>
        </div>

        <div className="info-section">
          <h3>What the Playbook Covers</h3>
          <div className="info-card">
            <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
              The Playbook defines the rules, structure, and decision framework behind your wealth system. It is not a guide to follow once — it is the operating manual you return to when making financial decisions.
            </p>
            <ul>
              <li>How your system is structured across accounts, allocations, and tools</li>
              <li>Decision rules for contributions, withdrawals, and rebalancing</li>
              <li>Lifecycle stage definitions and how they affect allocation</li>
              <li>Criteria for when to act and when to hold</li>
              <li>Integration points between planning, monitoring, and execution</li>
            </ul>
          </div>
        </div>

        <div className="info-section">
          <h3>How to Use It</h3>
          <div className="info-card">
            <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
              Review the Playbook when you set up your system, and revisit it periodically or when circumstances change. It should reflect your current rules — update it as your system evolves.
            </p>
            <p style={{ color: '#94a3b8' }}>
              The Playbook works alongside the other tools in this system. Net worth tracking, lifecycle planning, and capital allocation tools all operate within the framework it defines.
            </p>
          </div>
        </div>

        <div className="info-section">
          <h3>Listen</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            <div className="info-card">
              <h4 style={{ color: '#ffffff', marginBottom: '0.5rem' }}>Playbook Summary</h4>
              <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                A quick audio overview of the key points from the Playbook.
              </p>
              {!summaryUrl ? (
                <button
                  onClick={handleListenSummary}
                  disabled={loadingSummary}
                  className="btn-primary"
                  style={{ display: 'inline-block', cursor: loadingSummary ? 'wait' : 'pointer' }}
                >
                  {loadingSummary ? 'Loading...' : 'Listen to Summary'}
                </button>
              ) : (
                <audio controls style={{ width: '100%' }} src={summaryUrl}>
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>

            <div className="info-card">
              <h4 style={{ color: '#ffffff', marginBottom: '0.5rem' }}>Full Podcast</h4>
              <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                The full audio version of the Playbook. Covers the same material in a format you can take on the go.
              </p>
              {!podcastUrl ? (
                <button
                  onClick={handleListenPodcast}
                  disabled={loadingPodcast}
                  className="btn-primary"
                  style={{ display: 'inline-block', cursor: loadingPodcast ? 'wait' : 'pointer' }}
                >
                  {loadingPodcast ? 'Loading...' : 'Listen to Podcast'}
                </button>
              ) : (
                <audio controls style={{ width: '100%' }} src={podcastUrl}>
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>

            <div className="info-card">
              <h4 style={{ color: '#ffffff', marginBottom: '0.5rem' }}>Debate: Boring Investing</h4>
              <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Why simple, consistent strategies outperform constant tinkering — and why most investors benefit from doing less.
              </p>
              {!debateUrl ? (
                <button
                  onClick={handleListenDebate}
                  disabled={loadingDebate}
                  className="btn-primary"
                  style={{ display: 'inline-block', cursor: loadingDebate ? 'wait' : 'pointer' }}
                >
                  {loadingDebate ? 'Loading...' : 'Listen to Debate'}
                </button>
              ) : (
                <audio controls style={{ width: '100%' }} src={debateUrl}>
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '2rem' }}>
          {error && (
            <p style={{ color: '#f87171', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-primary"
            style={{ display: 'inline-block', cursor: downloading ? 'wait' : 'pointer', background: '#22c55e' }}
          >
            {downloading ? 'Opening Playbook...' : 'Open Playbook (PDF)'}
          </button>
        </div>
      </div>
    </div>
  );
};
